'use client';

import * as React from 'react';
import {Search, X} from 'lucide-react';
import {Button} from '@purpose/ui/components/button/button';
import {Input} from '@purpose/ui/components/input/input';
import {useDebouncedCallback} from 'use-debounce';
import {cn} from '@purpose/ui/lib/utils';

interface ExpandableSearchProps {
  results: {id: number; name?: string; title?: string}[];
  inputValue: string;
  setInputValue: (inp: string) => void;
  setSearchValue: (inp: string) => void;
  onSearchFieldVisibilityChange?: (isVisible: boolean) => void;
  width?: number | string;
  onClose?: () => void;
  placeholder?: string;
  buttonClassName?: string;
  inputClassName?: string;
  iconClassName?: string;
}

const ExpandableSearch = ({
  inputValue,
  setInputValue,
  setSearchValue,
  results,
  onSearchFieldVisibilityChange,
  width = 371,
  placeholder = 'Start searching...',
  buttonClassName,
  inputClassName,
  iconClassName
}: ExpandableSearchProps) => {
  const [isSearchVisible, setIsSearchVisible] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);

  React.useEffect(() => {
    onSearchFieldVisibilityChange?.(isSearchVisible);
  }, [isSearchVisible, onSearchFieldVisibilityChange]);

  const toggleSearch = () => setIsSearchVisible(prev => !prev);

  const debouncedSearch = useDebouncedCallback((value: string) => {
    setInputValue(value);
  }, 500);

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      setSearchValue(inputValue);
      setIsFocused(false);
    }
  };

  const handleSelect = (value: string) => {
    setInputValue(value);
    setSearchValue(value);
    setIsFocused(false);
  };

  return isSearchVisible ? (
    <div style={{width}} className="relative">
      <Input
        type="text"
        placeholder={placeholder}
        value={inputValue}
        onChange={e => {
          const value = e.target.value;
          setInputValue(value);
          debouncedSearch(value);
        }}
        autoFocus
        onKeyDown={handleKeyPress}
        onFocus={() => setIsFocused(true)}
        endicon={
          <div
            className="cursor-pointer"
            onClick={e => {
              e.stopPropagation();
              setInputValue('');
              debouncedSearch('');
              toggleSearch();
            }}
          >
            <X className="size-5 shrink-0 text-black" />
          </div>
        }
        onBlur={() => {
          if (!inputValue || inputValue?.trim()?.length == 0) {
            toggleSearch();
          }
          // allow click before closing
          setTimeout(() => setIsFocused(false), 100);
        }}
        className={cn('w-full bg-white', inputClassName)}
      />

      {/* AUTOCOMPLETE DROPDOWN */}
      {isFocused && results?.length > 0 && (
        <div className="absolute top-full left-0 z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-md">
          {results.map(item => {
            const value = item.name || item.title || '';

            return (
              <div
                key={item.id}
                onMouseDown={() => handleSelect(value)}
                className="cursor-pointer px-3 py-2 hover:bg-gray-100"
              >
                {value}
              </div>
            );
          })}
        </div>
      )}
    </div>
  ) : (
    <Button
      variant="ghost"
      size="sm"
      onClick={toggleSearch}
      className={cn(
        'text-secondary-40 hover:text-secondary-20 p-2',
        buttonClassName
      )}
      aria-label="Search"
    >
      <Search className={cn('h-6 w-6', iconClassName)} />
    </Button>
  );
};

export {ExpandableSearch};
export type {ExpandableSearchProps};
